import { BigNumber } from '@ethersproject/bignumber';
import { Dispatch, RefObject, SetStateAction, useEffect, useState } from 'react';
import { MutationFunction, useMutation } from '@tanstack/react-query';
import { SiloAsset } from 'queries/decoratedSilo';
import { SubmissionModalContext } from './SubmissionModal.context';
import { useModalState } from '@silo/ui';

export type MutationVariables = { asset: SiloAsset; amount: BigNumber; isFullAmount: boolean };
export type DoSubmit = MutationFunction<void, MutationVariables>;

interface UseSubmissionProps {
  doSubmit: DoSubmit;
  isFullAmount: boolean;
  isInvalid: boolean;
  normalizedTokenAmount: BigNumber | null;
  selectedAsset: SiloAsset;
  resetTokenAmount: () => void;
  tokenAmountRef: RefObject<HTMLInputElement>;
  isSubmissionModalEnabled: boolean;
}

interface UseSubmission {
  canSubmit: boolean;
  setPreventSubmit: Dispatch<SetStateAction<boolean>>;
  errorMessageFromSubmit?: string;
  submit: () => void;
  isPending: boolean;
  isSubmissionModalOpen: boolean;
  submissionModalContext: SubmissionModalContext;
}

export const useSubmission = ({
  doSubmit,
  isFullAmount,
  isInvalid,
  normalizedTokenAmount,
  selectedAsset,
  resetTokenAmount,
  tokenAmountRef,
  isSubmissionModalEnabled,
}: UseSubmissionProps): UseSubmission => {
  const [preventSubmit, setPreventSubmit] = useState(false);
  const [errorMessageFromSubmit, setErrorMessageFromSubmit] = useState<string | undefined>(undefined);
  const { isModalOpen, openModal, closeModal } = useModalState();

  const { isLoading, mutate } = useMutation<void, Error, MutationVariables>(doSubmit, {
    onSuccess: resetTokenAmount,
  });

  const canSubmit = !isInvalid && !isLoading && !preventSubmit;

  useEffect(() => {
    if (normalizedTokenAmount) setErrorMessageFromSubmit(undefined);
  }, [normalizedTokenAmount]);

  const submit = () => {
    if (!canSubmit) return;

    if (!normalizedTokenAmount) {
      setErrorMessageFromSubmit('Enter an amount');
      tokenAmountRef.current?.focus();
      return;
    }

    if (isSubmissionModalEnabled) {
      openModal();
      return;
    }

    mutate({
      asset: selectedAsset,
      amount: normalizedTokenAmount,
      isFullAmount,
    });
  };

  const submissionModalContext: SubmissionModalContext = {
    submit: () => {
      mutate({
        asset: selectedAsset,
        /**
         * casting as non-null - the submission modal only opens if there is a valid amount
         */
        amount: normalizedTokenAmount!,
        isFullAmount,
      });
      closeModal();
    },
    cancel: closeModal,
  };

  return {
    canSubmit,
    setPreventSubmit,
    errorMessageFromSubmit,
    submit,
    isPending: isLoading,
    isSubmissionModalOpen: isModalOpen,
    submissionModalContext,
  };
};
